import { HugeiconsIcon } from "@hugeicons/react";
import { motion } from "motion/react";

import type { IconSvgElement } from "@hugeicons/react";
import { CheckBullet } from "@/landing/ui/CheckBullet";
import { ICONS } from "@/landing/ui/FeatureCard";
import { SectionEyebrow } from "@/landing/ui/SectionEyebrow";

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

type UseCase = {
  index: string;
  icon: IconSvgElement;
  title: string;
  bullets: string[];
  resultNumber: string;
  resultLabel: string;
};

const CARDS: UseCase[] = [
  {
    index: "01",
    icon: ICONS.outreach,
    title: "The most tailored leadgen",
    bullets: [
      "Filter your ICPs with most in-depth social listening",
      "Naturally engage with your ICP's posts over weeks to increase touchpoints",
      "Iterate on your outreach to be 100% in your voice",
    ],
    resultNumber: "+46%",
    resultLabel: "booking rates",
  },
  {
    index: "02",
    icon: ICONS.create,
    title: "Content in your voice",
    bullets: [
      "Connect Wonda with your wiki to extract your saved posts and ideas",
      "Turn ideas into posts by automatically calibrating your tone of voice",
      "Create premium images & videos in your own branding (no slop)",
    ],
    resultNumber: "+78%",
    resultLabel: "impressions",
  },
  {
    index: "03",
    icon: ICONS.megaphone,
    title: "Influencer marketing",
    bullets: [
      "Search & naturally find microinfluencers in your feed",
      "Manage outreach & your relationships over DMs",
      "Benchmark their performance from their posts, filtered for your space",
    ],
    resultNumber: "+36%",
    resultLabel: "affiliate-linked revenue",
  },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function Wab2UseCases() {
  return (
    <section
      id="use-cases"
      className="scroll-mt-16 border-y border-[var(--hair)] bg-[var(--ground-elevated)] px-4 py-14 sm:px-6 md:py-20"
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="mx-auto max-w-2xl text-center">
          <div className="flex justify-center">
            <SectionEyebrow index="03">Use cases</SectionEyebrow>
          </div>
          <h2
            className="mt-5 text-2xl font-semibold tracking-[-0.03em] text-balance sm:text-3xl lg:text-4xl"
            style={{ color: "var(--cli-text)" }}
          >
            What people run on Wonda
          </h2>
        </div>

        <div className="mx-auto mt-14 grid max-w-5xl gap-4 sm:grid-cols-3">
          {CARDS.map((card) => (
            <div
              key={card.index}
              className="group flex h-full flex-col border border-[var(--hair-strong)] bg-[var(--surface)] p-7 transition-all duration-300 hover:-translate-y-0.5 hover:border-[var(--green)]"
            >
              <span
                aria-hidden
                className="inline-flex size-11 items-center justify-center text-[var(--green)] transition-transform duration-300 group-hover:scale-105"
                style={{
                  backgroundColor:
                    "color-mix(in srgb, var(--green) 12%, transparent)",
                }}
              >
                <HugeiconsIcon
                  icon={card.icon}
                  className="size-6"
                  strokeWidth={1.6}
                />
              </span>
              <h3
                className="mt-5 text-lg font-semibold tracking-tight sm:min-h-[3.5rem]"
                style={{ color: "var(--cli-text)" }}
              >
                {card.title}
              </h3>
              <ul className="mt-4 space-y-3">
                {card.bullets.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2.5">
                    <CheckBullet />
                    <span
                      className="text-sm leading-relaxed"
                      style={{ color: "var(--cli-text-secondary)" }}
                    >
                      {bullet}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto border-t border-[var(--hair)] pt-6">
                <div className="flex items-center gap-1.5">
                  <svg
                    className="size-5"
                    viewBox="0 0 20 20"
                    fill="none"
                    aria-hidden
                  >
                    <path
                      d="M4 13.5 8.5 9l3 3L16 6.5M16 6.5h-3.5M16 6.5V10"
                      stroke="var(--green)"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span
                    className="text-3xl font-bold tracking-tight tabular-nums sm:text-4xl"
                    style={{ color: "var(--green)" }}
                  >
                    {card.resultNumber}
                  </span>
                </div>
                <div
                  className="mt-1 text-sm"
                  style={{ color: "var(--cli-text-muted)" }}
                >
                  {card.resultLabel}
                </div>
              </div>
            </div>
          ))}
        </div>

        <p
          className="mx-auto mt-12 max-w-2xl text-center text-base sm:text-lg"
          style={{ color: "var(--cli-text-secondary)" }}
        >
          Plus pretty much anything else you&apos;re doing manually. Still you,
          just amplified.
        </p>
      </motion.div>
    </section>
  );
}
